import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HeaderMobileV1 from './HeaderMobile_v1';
import HeaderMobileV2 from './HeaderMobile_v2';
import HeaderDesktop from './HeaderDesktop';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';
import DesktopNavbar from '@/shared/features/menu/presentation/components/DesktopNavbar';
import { useStickyOnScroll } from '@/core/hooks/useStickyOnScroll';
import { useScrollDirection } from '@/core/hooks/useScrollDirection';
import { useAppSelector } from '@/app/store/store';
import SearchModal from '@/features/search/presentation/components/SearchModal';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { trackSearchedForProduct } from '@/features/search/constants';
import { navigateToSearchPage } from '@/features/search/utils/searchNavigation';
import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';

/** Paths where the page has its own sticky bar (e.g. PLP); header should scroll away so that bar can stick to top */
function isPlpOrStickyPage(pathname: string): boolean {
  const p = pathname.split('?')[0];
  return (
    p.startsWith('/jewellery') ||
    p.startsWith('/search') ||
    p.startsWith('/plp') ||
    /^\/recently-viewed\/?$/.test(p) ||
    /^\/stores\/[^/]+\/catalog/.test(p)
  );
}

type HeaderLoginMode = 'login' | 'signup';

interface HeaderProps {
  onOpenLogin?: (mode?: HeaderLoginMode) => void;
}

export default function Header({ onOpenLogin }: HeaderProps) {
  // Select the mobile icon variant from PLP experiment assignment, not the current route.
  // V3/V4 use V2 wherever this shared Header renders; desktop and existing callbacks stay unchanged.
  const { plpVariant } = useMultivariateExperiment();
  const HeaderMobile = plpVariant === 'V3' || plpVariant === 'V4' ? HeaderMobileV2 : HeaderMobileV1;
  const { userContext, isLoading: isUserContextLoading } = useUserContext();
  const { analytics } = useAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const isPlpPage = isPlpOrStickyPage(location.pathname);
  const isSearchModalOpen = useAppSelector((s) => (s as Record<string, { isSearchModalOpen?: boolean }>).searchUi?.isSearchModalOpen ?? false);
  const [isHeaderSearchModalOpen, setIsHeaderSearchModalOpen] = useState(false);

  // Track when we've scrolled past the header (sentinel) and scroll direction.
  const { sentinelRef, isSticky } = useStickyOnScroll();
  const scrollDirection = useScrollDirection();

  // Default behavior (non-PLP): header sticks when sentinel hits top.
  const headerIsFixed = (plpVariant === 'V3' || plpVariant === 'V4')
    ? isSticky
    : isPlpPage
    ? isSticky && scrollDirection === 'up'
    : isSticky;

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    trackSearchedForProduct(analytics, trimmed);
    navigateToSearchPage(trimmed, (url) => navigate(url));
  };

  return (
    <>
      {/* Sentinel for mobile sticky detection */}
      <div ref={sentinelRef} className="h-0" aria-hidden />

      <div
        className={`hdr-mobile bg-white shadow-sm z-50 ${
          headerIsFixed ? 'fixed top-0 left-0 right-0' : ''
        } ${isSearchModalOpen ? 'invisible' : ''}`}
      >
        <HeaderMobile
          cartCount={userContext?.cartCount ?? 0}
          wishlistCount={userContext?.wishlistCount ?? 0}
          isLoggedIn={userContext?.isLoggedIn ?? false}
          userName={userContext?.userName}
          onSearch={handleSearch}
          onSearchIconClick={() => setIsHeaderSearchModalOpen(true)}
          onOpenLogin={onOpenLogin}
        />
      </div>

      {/* Header search modal — rendered outside hdr-mobile so it's never hidden by it */}
      <SearchModal
        isOpen={isHeaderSearchModalOpen}
        onClose={() => setIsHeaderSearchModalOpen(false)}
        onSearch={(q) => { setIsHeaderSearchModalOpen(false); handleSearch(q); }}
      />

      {/* Spacer to prevent content jump when header becomes fixed */}
      {headerIsFixed && <div className="hdr-mobile h-[54px]" aria-hidden />}

      <header className="w-full overflow-visible shadow-sm">
        <div className="hdr-desktop">
          <HeaderDesktop
            cartCount={userContext?.cartCount ?? 0}
            wishlistCount={userContext?.wishlistCount ?? 0}
            isLoggedIn={userContext?.isLoggedIn ?? false}
            isSoftLoggedIn={userContext?.isSoftLoggedIn ?? false}
            userName={userContext?.userName}
            isUserContextLoading={isUserContextLoading}
            onSearch={handleSearch}
            onOpenLogin={onOpenLogin}
          />
        </div>
      </header>
      <DesktopNavbar className="hdr-desktop" />
    </>
  );
}
